import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Stack,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { devBatchSync, getDevOptions } from '../../services/calendar.service';
import type { BatchSyncPayload, BatchSyncResponseData, DevClientOption, DevEventTypeOption } from '../../services/calendar.service';
import { ApiError, getErrorMessage } from '@/utils/api-response.util';
import { toastService } from '@services/toast.service';
import { BaseDialog } from '@components/common/BaseDialog';
import { ClientAutocomplete } from './ClientAutocomplete';

const makeClientEventId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const nowLdt = () => new Date().toISOString().slice(0, 19);

const SELECTED_CLIENT_STORAGE_KEY = 'dev-batch-sync-client';

const loadStoredClient = (): DevClientOption | null => {
  try {
    const raw = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DevClientOption) : null;
  } catch {
    return null;
  }
};

// Довідник структур по типу події. Додавати новий тип (workout, food, ...) — один новий запис тут.
interface BatchSyncFieldDoc {
  name: string;
  type: string;
  required: boolean;
  note: string;
}

const TYPE_DOCS: { type: string; label: string; fields: BatchSyncFieldDoc[] }[] = [
  {
    type: 'feeling',
    label: 'feeling — думки / самопочуття',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'title', type: 'string', required: true, note: '1–1000 символів. Текст думки.' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync — реальний момент, коли подія відбулась офлайн (сервер не підставляє "поточний час").' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
    ],
  },
  {
    type: 'fluid',
    label: 'fluid — вода / рідина',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'qty', type: 'number', required: true, note: 'Кількість рідини в мл, 1–5000.' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync (як і для всіх типів).' },
      { name: 'title', type: 'string', required: false, note: 'Якщо відсутнє — автогенерується з qty (напр. "Вода 500мл").' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
      { name: 'isCompleted', type: 'boolean', required: false, note: 'Чи позначити подію як виконану.' },
    ],
  },
  {
    type: 'task',
    label: 'task — завдання',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'title', type: 'string', required: true, note: '1–1000 символів. Назва завдання.' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync (як і для всіх типів).' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
      { name: 'isCompleted', type: 'boolean', required: false, note: 'Чи позначити завдання як виконане.' },
    ],
  },
  {
    type: 'medication',
    label: 'medication — препарати',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'medicationId / customName', type: 'number / string', required: true, note: 'Рівно одне з двох: ID препарату з бібліотеки АБО назва кастомного препарату. Не обидва, не жодного.' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync (як і для всіх типів).' },
      { name: 'qty', type: 'number', required: false, note: '1–1000. Кількість (наприклад, таблеток).' },
      { name: 'dosage', type: 'string', required: false, note: 'Дозування, напр. "1000iu".' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
      { name: 'isCompleted', type: 'boolean', required: false, note: 'Чи позначити подію як виконану.' },
    ],
  },
  {
    type: 'food',
    label: 'food — їжа (AI-залежний тип!)',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'text', type: 'string', required: true, note: 'Сирий текст (напр. "зелений борщ") — AI розпізнає продукти і рахує БЖУ за 1 виклик (як /events/food/complex, startFromStep=1).' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync (як і для всіх типів).' },
      { name: 'rawInput', type: 'string', required: false, note: 'Якщо відсутнє — береться з text.' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
      { name: 'isCompleted', type: 'boolean', required: false, note: 'Чи позначити подію як виконану.' },
      { name: 'simulateFailure', type: 'boolean', required: false, note: 'ТІЛЬКИ ДЛЯ ТЕСТУВАННЯ. Симулює невдачу AI-запиту без реального AI-виклику — для перевірки обриву пакету.' },
    ],
  },
  {
    type: 'workout',
    label: 'workout — тренування (AI-залежний тип!)',
    fields: [
      { name: 'clientEventId', type: 'string (UUID)', required: true, note: 'Унікальний на девайсі. Повертається у відповіді для мапінгу serverId ↔ clientEventId.' },
      { name: 'text', type: 'string', required: true, note: 'Сирий текст (напр. "віджимання 10") — AI розпізнає вправи і рахує калорії за 1 виклик (як /events/workout/complex, startFromStep=1).' },
      { name: 'scheduledLdt', type: 'string (T3, без Z)', required: true, note: '"YYYY-MM-DDTHH:mm:ss". Обов\'язкове для sync (як і для всіх типів).' },
      { name: 'rawInput', type: 'string', required: false, note: 'Якщо відсутнє — береться з text.' },
      { name: 'calendarId', type: 'number', required: false, note: 'Якщо відсутнє — дефолтний календар клієнта.' },
      { name: 'isCompleted', type: 'boolean', required: false, note: 'Чи позначити подію як виконану.' },
      { name: 'simulateFailure', type: 'boolean', required: false, note: 'ТІЛЬКИ ДЛЯ ТЕСТУВАННЯ. Симулює невдачу AI-запиту без реального AI-виклику — для перевірки обриву пакету.' },
    ],
  },
];

export const DevBatchSyncTest: React.FC = () => {
  const [client, setClientState] = useState<DevClientOption | null>(loadStoredClient);

  const setClient = (next: DevClientOption | null) => {
    setClientState(next);
    if (next) {
      localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(SELECTED_CLIENT_STORAGE_KEY);
    }
  };

  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [data, setData] = useState<BatchSyncResponseData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [eventTypes, setEventTypes] = useState<DevEventTypeOption[]>([]);
  const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
  const [checkedTypes, setCheckedTypes] = useState<Set<string>>(new Set());

  const [rawRequest, setRawRequest] = useState<unknown>(null);
  const [rawResponse, setRawResponse] = useState<unknown>(null);
  const [rawModalOpen, setRawModalOpen] = useState(false);

  useEffect(() => {
    getDevOptions()
      .then((opts) => {
        const types = opts.eventTypes.filter((t) => t.code !== 'appointment');
        setEventTypes(types);
        setSupportedTypes(opts.supportedBatchSyncTypes);
        setCheckedTypes(new Set(opts.supportedBatchSyncTypes));
      })
      .catch((err) => console.error('Failed to load dev options:', err));
  }, []);

  const toggleType = (code: string, checked: boolean) => {
    setCheckedTypes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(code); else next.delete(code);
      return next;
    });
  };

  const runBatch = async (key: string, payload: BatchSyncPayload) => {
    if (!client) {
      // Кнопки вже задизейблені без клієнта — це запасний запобіжник.
      setError('Спочатку виберіть клієнта');
      return;
    }

    setLoadingKey(key);
    setError(null);
    setData(null);
    setRawRequest({ endpoint: '/calendars/dev/batch-sync', body: { userId: client.id, ...payload } });

    try {
      const result = await devBatchSync(client.id, payload);
      setData(result);
      setRawResponse(result);
    } catch (err: unknown) {
      console.error('Batch sync test error:', err);
      setError(getErrorMessage(err, 'Не вдалося виконати запит'));
      setRawResponse(err instanceof ApiError ? { statusCode: err.statusCode, ...err.response } : { message: getErrorMessage(err, 'Невідома помилка') });
    } finally {
      setLoadingKey(null);
      setRawModalOpen(true);
    }
  };

  // Кожен тип додається в пакет лише якщо відповідний чекбокс позначений — так чекбокси
  // реально впливають на те, що відправляється, а не лише декоративні.
  const feelingChecked = checkedTypes.has('feeling');
  const fluidChecked = checkedTypes.has('fluid');
  const taskChecked = checkedTypes.has('task');
  const medicationChecked = checkedTypes.has('medication');
  const foodChecked = checkedTypes.has('food');
  const workoutChecked = checkedTypes.has('workout');

  const handleValidBatch = () =>
    runBatch('valid', {
      feeling: feelingChecked ? [
        { clientEventId: makeClientEventId(), title: 'Відчуваю себе добре сьогодні', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), title: 'Енергійний настрій після прогулянки', scheduledLdt: nowLdt() },
      ] : undefined,
      fluid: fluidChecked ? [
        { clientEventId: makeClientEventId(), qty: 250, scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), qty: 500, title: 'Вода з лимоном', scheduledLdt: nowLdt() },
      ] : undefined,
      task: taskChecked ? [
        { clientEventId: makeClientEventId(), title: 'Записатися до лікаря', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), title: 'Купити продукти', scheduledLdt: nowLdt() },
      ] : undefined,
      medication: medicationChecked ? [
        { clientEventId: makeClientEventId(), customName: 'Аспірин', qty: 1, scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), customName: 'Вітамін D', dosage: '1000iu', scheduledLdt: nowLdt() },
      ] : undefined,
      // food/workout — AI-залежні, повільніші (1-2 AI-виклики на item). Обробляються
      // останніми, після всіх DB-only типів.
      food: foodChecked ? [
        { clientEventId: makeClientEventId(), text: 'зелений борщ', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), text: 'омлет з 2 яєць', scheduledLdt: nowLdt() },
      ] : undefined,
      workout: workoutChecked ? [
        { clientEventId: makeClientEventId(), text: 'віджимання 10', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), text: 'біг 20 хвилин', scheduledLdt: nowLdt() },
      ] : undefined,
    });

  const handleMixedBatch = () =>
    runBatch('mixed', {
      feeling: feelingChecked ? [
        { clientEventId: makeClientEventId(), title: 'Валідний запис — все добре', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), title: '', scheduledLdt: nowLdt() }, // порожній title — має впасти лише цей item
        { clientEventId: makeClientEventId(), title: 'Ще один валідний запис', scheduledLdt: nowLdt() },
      ] : undefined,
      fluid: fluidChecked ? [
        { clientEventId: makeClientEventId(), qty: 300, scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), qty: 0, scheduledLdt: nowLdt() }, // qty=0 — невалідне, має впасти лише цей item
      ] : undefined,
      task: taskChecked ? [
        { clientEventId: makeClientEventId(), title: 'Подзвонити мамі', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), title: '', scheduledLdt: nowLdt() }, // порожній title — має впасти лише цей item
      ] : undefined,
      medication: medicationChecked ? [
        { clientEventId: makeClientEventId(), customName: 'Магній', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), scheduledLdt: nowLdt() }, // ні medicationId, ні customName — невалідне, має впасти лише цей item
      ] : undefined,
      // Порожній text — помилка ВХІДНИХ ДАНИХ (BadRequestException ще до AI-виклику),
      // тому НЕ обриває пакет: retryable:false, інші food/workout-items (і все інше) обробляються далі.
      food: foodChecked ? [
        { clientEventId: makeClientEventId(), text: 'грецький салат', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), text: '', scheduledLdt: nowLdt() },
      ] : undefined,
      workout: workoutChecked ? [
        { clientEventId: makeClientEventId(), text: 'присідання 20', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), text: '', scheduledLdt: nowLdt() },
      ] : undefined,
    });

  const handleEmptyBatch = () => runBatch('empty', {});

  // Демонструє справжній обрив пакету: 1-й food item валідний, 2-й симулює невдачу
  // AI-запиту (simulateFailure, без реального AI-виклику) → aborted:true, а все, що
  // йде ПІСЛЯ (3-й food, увесь workout) — retryable:true без спроби обробки.
  const handleAiAbortBatch = () =>
    runBatch('ai-abort', {
      food: foodChecked ? [
        { clientEventId: makeClientEventId(), text: 'тарілка плову', scheduledLdt: nowLdt() },
        { clientEventId: makeClientEventId(), text: 'не має значення', scheduledLdt: nowLdt(), simulateFailure: true },
        { clientEventId: makeClientEventId(), text: 'цей item не буде навіть спробуваний', scheduledLdt: nowLdt() },
      ] : undefined,
      workout: workoutChecked ? [
        { clientEventId: makeClientEventId(), text: 'теж не буде спробуваний', scheduledLdt: nowLdt() },
      ] : undefined,
    });

  const handleCopyRawJson = () => {
    navigator.clipboard.writeText(JSON.stringify({ request: rawRequest, response: rawResponse }, null, 2));
    toastService.success('JSON скопійовано в буфер обміну');
  };

  const isLoading = loadingKey !== null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Тестування пакетної синхронізації (Batch Sync)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Перевірка <code>POST /calendars/events/batch-sync</code>: офлайн-пакет подій (<code>feeling</code>, <code>fluid</code>, <code>task</code>, <code>medication</code>, <code>food</code>, <code>workout</code>), кожен item обробляється окремо — один невалідний не валить весь пакет (окрім AI-збою в <code>food</code>/<code>workout</code> — той обриває залишок пакету).
      </Typography>

      <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Довідник для фронтенд-розробника: структура запиту/відповіді</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom>Загальне</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            <ul style={{ marginTop: 4, paddingLeft: 20 }}>
              <li>Тіло запиту — об'єкт з одним опціональним масивом на кожен тип події: <code>{'{ feeling: [...], fluid: [...] }'}</code>. Ключ масиву = код типу події в системі.</li>
              <li>Кожен item будь-якого типу обов'язково містить <code>clientEventId</code> (UUID з девайсу) — використовується для мапінгу результату назад на локальний запис.</li>
              <li><code>scheduledLdt</code> — обов'язковий для **усіх** типів: сервер ніколи не підставляє "поточний час" замість нього, бо подія вже відбулась офлайн у конкретний момент.</li>
              <li>Кожен item обробляється незалежно: один невалідний не валить весь пакет. Відповідь завжди містить запис для кожного відправленого item.</li>
              <li><b>AI-залежні типи (<code>food</code>, <code>workout</code>)</b> обробляються окремою фазою, строго послідовно (ніколи паралельно — щоб не перевантажити AI-провайдера). Перша справжня AI-помилка (timeout/мережа, не помилка вхідних даних) <b>обриває весь залишок пакету</b> (будь-якого типу) — далі йде <code>aborted: true</code> у відповіді, а пропущені items — з <code>retryable: true</code>. Той самий ефект і при перевищенні загального дедлайну обробки запиту (20с).</li>
              <li><code>retryable: true</code> у item — варто відправити цей самий item повторно наступним sync-запитом. <code>retryable: false</code> — проблема в даних, повтор того самого не допоможе.</li>
            </ul>
          </Typography>

          <Typography variant="subtitle2" gutterBottom>Відповідь (<code>data</code>)</Typography>
          <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflowX: 'auto', mb: 2 }}>
{`{
  total: number;
  succeeded: number;
  failed: number;
  aborted: boolean;    // true — пакет перервано (AI-збій або >20с), є непроброблені items
  results: Array<{
    clientEventId: string;
    type: string;
    success: boolean;
    eventId?: number;    // присутній лише якщо success === true
    error?: string;      // присутній лише якщо success === false
    retryable?: boolean; // true — повторити цей item наступним sync; false — проблема в даних
  }>;
}`}
          </Box>

          <Typography variant="subtitle2" gutterBottom>Структури по типу події</Typography>
          {TYPE_DOCS.map((doc) => (
            <Box key={doc.type} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600} gutterBottom>{doc.label}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Поле</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Обов'язкове</TableCell>
                    <TableCell>Примітка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {doc.fields.map((f) => (
                    <TableRow key={f.name}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{f.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{f.type}</TableCell>
                      <TableCell>{f.required ? 'так' : 'ні'}</TableCell>
                      <TableCell>{f.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}

          <Typography variant="caption" color="text.secondary">
            Повна документація: <code>api/src/modules/calendars/doc/batch-sync.md</code>
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ maxWidth: 480, mb: 3 }}>
        <ClientAutocomplete value={client} onChange={setClient} />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Типи подій для генерації
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Непідтримані типи задизейблені — для них ще не реалізований processor у <code>CalendarBatchSyncService</code>.
        </Typography>
        <FormGroup row>
          {eventTypes.map((t) => {
            const supported = supportedTypes.includes(t.code);
            const control = (
              <Checkbox
                checked={checkedTypes.has(t.code)}
                disabled={!supported}
                onChange={(e) => toggleType(t.code, e.target.checked)}
              />
            );
            return (
              <Tooltip key={t.code} title={supported ? '' : 'Ще не підтримується в batch-sync'}>
                <FormControlLabel control={control} label={t.name} />
              </Tooltip>
            );
          })}
        </FormGroup>
      </Paper>

      {client ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Події будуть створені від імені клієнта <b>{client.name}</b> (#{client.id}) через DEV-ендпоінт <code>/calendars/dev/batch-sync</code>.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Спочатку виберіть клієнта вище — без нього відправка пакету недоступна.
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            1. Валідний пакет (2 коректні думки)
          </Typography>
          <Button variant="contained" onClick={handleValidBatch} disabled={isLoading || !client}>
            {loadingKey === 'valid' ? <CircularProgress size={24} color="inherit" /> : 'Відправити валідний пакет'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            2. Змішаний пакет (2 валідні + 1 з порожнім текстом)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Очікується: <code>succeeded: 2, failed: 1</code> — невалідний item у відповіді з конкретною помилкою, інші два збережені.
          </Typography>
          <Button variant="outlined" color="warning" onClick={handleMixedBatch} disabled={isLoading || !client}>
            {loadingKey === 'mixed' ? <CircularProgress size={24} /> : 'Відправити змішаний пакет'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            3. Порожній пакет (без жодного типу подій)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Очікується: <code>400 Bad Request</code>.
          </Typography>
          <Button variant="outlined" color="error" onClick={handleEmptyBatch} disabled={isLoading || !client}>
            {loadingKey === 'empty' ? <CircularProgress size={24} /> : 'Відправити порожній пакет'}
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            4. AI-збій → обрив пакету (потребує food і/або workout)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1-й food item валідний (succeeded), 2-й симулює справжню невдачу AI-запиту (<code>simulateFailure</code>, без реального AI-виклику) — очікується <code>aborted: true</code>, а 3-й food і весь workout — <code>retryable: true</code> без спроби обробки.
          </Typography>
          <Button variant="outlined" color="error" onClick={handleAiAbortBatch} disabled={isLoading || !client || (!foodChecked && !workoutChecked)}>
            {loadingKey === 'ai-abort' ? <CircularProgress size={24} /> : 'Симулювати AI-збій'}
          </Button>
        </Paper>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          <Typography variant="subtitle2" fontWeight="bold">Помилка:</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {data && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
            <Chip label={`Всього: ${data.total}`} />
            <Chip label={`Успішно: ${data.succeeded}`} color="success" />
            <Chip label={`Невдало: ${data.failed}`} color={data.failed > 0 ? 'error' : 'default'} />
            {data.aborted && <Chip label="Пакет перервано (AI-збій/дедлайн)" color="error" />}
            <Button size="small" onClick={() => setRawModalOpen(true)} sx={{ ml: 'auto' }}>
              Показати сирий JSON
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>clientEventId</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>eventId / Помилка</TableCell>
                <TableCell>Retryable</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((r) => (
                <TableRow key={r.clientEventId}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.clientEventId}</TableCell>
                  <TableCell>{r.type}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.success ? 'OK' : 'FAIL'} color={r.success ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell>{r.success ? r.eventId : r.error}</TableCell>
                  <TableCell>
                    {!r.success && (
                      <Chip size="small" label={r.retryable ? 'так' : 'ні'} color={r.retryable ? 'warning' : 'default'} variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <BaseDialog
        open={rawModalOpen}
        onClose={() => setRawModalOpen(false)}
        title="Тіло запиту та відповідь сервера"
        maxWidth="md"
        actions={<Button onClick={handleCopyRawJson}>Скопіювати JSON</Button>}
      >
        <Typography variant="subtitle2" gutterBottom>Відправлено (request)</Typography>
        <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', mb: 2 }}>
          {JSON.stringify(rawRequest, null, 2)}
        </Box>

        <Typography variant="subtitle2" gutterBottom>Отримано (response)</Typography>
        <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', m: 0 }}>
          {JSON.stringify(rawResponse, null, 2)}
        </Box>
      </BaseDialog>
    </Box>
  );
};
